import { formatFechaCorta, formatFechaHora, formatMonto } from '../utils/formatters.js';

/**
 * Pure builder: settlement row (`listLiquidaciones` shape, includes
 * `empleados: { nombre }`) -> comprobante view-model. NO DOM, NO
 * `Date.now()`, NO network call, NO monetary computation — only formatting
 * of already-frozen values (spec `payroll-settlement-comprobante`,
 * "Deterministic Pure Policy Output"; design.md AD2/AD3/AD17).
 *
 * Money and quantity fields in the returned view-model are ALREADY
 * formatted strings (`formatMonto`/`formatFechaCorta`/local helpers below),
 * mirroring the "which lines exist and which are omitted" assertion style
 * design.md AD2 calls for — `ComprobanteLiquidacionView.js` only assembles
 * HTML and escapes, it does not reformat anything.
 */

const MODALIDAD_LABELS = {
    hora: 'Por hora',
    dia: 'Por día',
    fijo: 'Sueldo fijo',
};

const FOOTER_TEXT = 'Documento interno de control de horas. No constituye recibo de sueldo.';

/**
 * `periodo_desde`/`periodo_hasta` son columnas Postgres `date` — SIN
 * componente horario, serializadas como el literal `'YYYY-MM-DD'`. Formatear
 * ESO con `new Date(...)` + `Intl`/`timeZone` las trataría como medianoche
 * UTC y, al convertir a America/Argentina/Buenos_Aires (UTC-3), SIEMPRE las
 * correría un día para atrás — no es el mismo caso que `formatFechaCorta`
 * (que sí espera un instante real con hora). Por eso esto es manipulación de
 * string pura, nunca `Date`/`Intl`.
 * @param {string|null|undefined} fechaSql
 * @returns {string}
 */
function formatFechaPeriodo(fechaSql) {
    if (typeof fechaSql !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(fechaSql)) return '—';
    const [anio, mes, dia] = fechaSql.split('-');
    return `${dia}/${mes}/${anio}`;
}

/**
 * Minutos exactos -> "Xh YYm" (mismo redondeo de presentación que
 * `FichadasView.js#formatMinutos` — duplicado a propósito, mismo criterio
 * AD4: es formateo, no cómputo de negocio). Nulo/no-finito -> el mismo
 * placeholder que `formatMonto` (review-ddffaacc59304b7d): nunca "NaNh NaNm"
 * ni un silencioso "0h 00m" para un valor ausente.
 * @param {number|null|undefined} minutos
 * @returns {string}
 */
function formatMinutosHoras(minutos) {
    if (minutos === null || minutos === undefined || !Number.isFinite(Number(minutos))) return '—';
    const totalRedondeado = Math.round(Number(minutos));
    const horas = Math.floor(totalRedondeado / 60);
    const mins = totalRedondeado % 60;
    return `${horas}h ${String(mins).padStart(2, '0')}m`;
}

/**
 * Líneas de cantidad (horas o días) + la línea de multiplicador de feriado,
 * spec "Modalidad-Specific Blocks".
 * @param {object} detalle
 * @returns {Array<{label: string, valor: string|number|null}>}
 */
function construirCantidades(detalle) {
    const cantidades = [];
    if (detalle.modo === 'hora') {
        cantidades.push({ label: 'Horas normales', valor: formatMinutosHoras(detalle.minutos_normales) });
        cantidades.push({ label: 'Horas en feriado', valor: formatMinutosHoras(detalle.minutos_feriado) });
    } else if (detalle.modo === 'dia') {
        cantidades.push({ label: 'Días normales', valor: detalle.dias_normales ?? 0 });
        cantidades.push({ label: 'Días feriado', valor: detalle.dias_feriado ?? 0 });
    }

    const tiempoFeriado = (detalle.minutos_feriado ?? 0) + (detalle.dias_feriado ?? 0);
    if ((detalle.multiplicador_aplicado ?? 1) > 1 && tiempoFeriado > 0) {
        cantidades.push({ label: `Feriados pagados al ×${detalle.multiplicador_aplicado}`, valor: null });
    }
    return cantidades;
}

/**
 * Líneas de monto: valor fijo (solo `fijo`), bruto, consumos, adelantos,
 * neto (énfasis). `consumos`/`adelantos` son los totales YA congelados de la
 * fila (incluyen el excedente de tope recuperado, ver payroll-policy.js) —
 * nunca 0 omitido, siempre presentes (spec "Core Content Always Present").
 * @param {object} row
 * @param {object} detalle
 * @returns {Array<{label: string, monto: string, enfasis?: boolean}>}
 */
function construirMontos(row, detalle) {
    const montos = [];
    if (detalle.modo === 'fijo') {
        montos.push({ label: 'Valor fijo del período', monto: formatMonto(detalle.valor_fijo) });
    }
    montos.push({ label: 'Bruto', monto: formatMonto(row.bruto) });
    montos.push({ label: 'Consumos', monto: formatMonto(row.consumos) });
    montos.push({ label: 'Adelantos', monto: formatMonto(row.adelantos) });
    // Spec liquidacion-otros-descuentos (B6, D5): congelado en `detalle`;
    // una liquidación anterior a este cambio no tiene la clave y descontó 0.
    montos.push({ label: 'Otros descuentos', monto: formatMonto(detalle.otros_descuentos ?? 0) });
    montos.push({ label: 'Neto', monto: formatMonto(row.neto), enfasis: true });
    return montos;
}

/**
 * Bloque de tope de descuento, spec "Discount Cap Block Conditional
 * Rendering": ausente (nunca `0`) sin `descuento_consumos`; reemplazado por
 * el marcador `sinFudoCliente` en esa fuente; si no, tope/imputado
 * (sumado de TODOS los meses de `por_mes`)/excedente.
 * @param {object|undefined} descuentoConsumos
 * @returns {{topeMensual: string, imputado: string, excedente: string}|{sinFudoCliente: true}|null}
 */
function construirCap(descuentoConsumos) {
    if (!descuentoConsumos) return null;
    if (descuentoConsumos.fuente === 'sin-fudo-cliente-id') return { sinFudoCliente: true };

    const imputado = Object.values(descuentoConsumos.por_mes ?? {}).reduce(
        (acc, m) => acc + (m?.descuento_imputado ?? 0),
        0,
    );
    return {
        topeMensual: formatMonto(descuentoConsumos.tope_mensual),
        imputado: formatMonto(imputado),
        excedente: formatMonto(descuentoConsumos.excedente_total ?? 0),
    };
}

/**
 * Ítems de consumos (`detalle.consumos_items`, forma
 * `{saleId,paymentId,amount,createdAt}` de `fetchConsumosBatch` en
 * `sales-ledger.js`, persistida tal cual por `api/liquidacion.js`) ->
 * `{fecha, monto}`, sin ids (design.md AD17). La clave de fecha es
 * `createdAt`; `saleCreatedAt` pertenece a los ítems de DESCUENTO, que no
 * se persisten aquí (review-ddffaacc59304b7d).
 * @param {Array<object>|undefined} items
 * @returns {Array<{fecha: string, monto: string}>}
 */
function construirConsumosItems(items) {
    const lista = Array.isArray(items) ? items : [];
    return lista
        .filter((it) => it !== null && typeof it === 'object')
        .map((it) => ({ fecha: formatFechaCorta(it.createdAt), monto: formatMonto(it.amount) }));
}

/**
 * Ítems de adelantos (`detalle.adelantos_items`) -> `{fecha, monto}`, sin
 * ids (design.md AD17). PERMANENTEMENTE bimodal (S4/AD10) — `liquidaciones`
 * es append-only, así que las filas congeladas ANTES del corte S4 quedan
 * para siempre con la forma vieja de Fudo:
 *   - forma NUEVA (S4 en adelante, `adelantos` propia): `{adelantoId, amount,
 *     fecha:'YYYY-MM-DD', nota}` — `fecha` es un `date` de Postgres, formateado
 *     con `formatFechaPeriodo` (string slicing, nunca `Date`/`Intl`) para no
 *     reintroducir el off-by-one de un día que `formatFechaCorta` produciría
 *     al tratar una fecha-sin-hora como medianoche UTC.
 *   - forma VIEJA (congelada antes del corte, `expenses-ledger.js`):
 *     `{expenseId, paymentIds, amount, paymentDates}` — un gasto puede
 *     agrupar varios pagos con varias fechas; se muestra la MÁS TEMPRANA
 *     (determinística sin importar el orden en que `paymentDates` haya
 *     llegado, spec "Deterministic Pure Policy Output"), vía
 *     `formatFechaCorta` (SÍ espera un instante real con hora, a diferencia
 *     de `fecha`).
 * El dispatch es por PRESENCIA de la clave `fecha` — nunca por `expenseId`
 * ausente, para no depender de que la forma vieja nunca gane esa clave.
 * @param {Array<object>|undefined} items
 * @returns {Array<{fecha: string, monto: string}>}
 */
function construirAdelantosItems(items) {
    const lista = Array.isArray(items) ? items : [];
    return lista
        .filter((it) => it !== null && typeof it === 'object')
        .map((it) => {
            if (typeof it.fecha === 'string') {
                return { fecha: formatFechaPeriodo(it.fecha), monto: formatMonto(it.amount) };
            }
            const fechas = (it.paymentDates ?? []).slice().sort();
            return { fecha: formatFechaCorta(fechas[0]), monto: formatMonto(it.amount) };
        });
}

/**
 * Otros descuentos congelados (spec liquidacion-otros-descuentos, B6):
 * `{descuentoId, amount, fecha, concepto}` -> `{fecha, concepto, monto}`.
 * Una liquidación anterior a este cambio no tiene la clave -> `[]`.
 * @param {Array<object>|undefined} items
 * @returns {Array<{fecha: string, concepto: string, monto: string}>}
 */
function construirOtrosDescuentosItems(items) {
    const lista = Array.isArray(items) ? items : [];
    return lista
        .filter((it) => it !== null && typeof it === 'object')
        .map((it) => ({ fecha: formatFechaPeriodo(it.fecha), concepto: it.concepto ?? '', monto: formatMonto(it.amount) }));
}

/**
 * Nota de warnings sin identificadores crudos (design.md AD17, spec
 * "Warnings and Void Marker"): "Hay N gasto(s) de Fudo sin mapear; revisá el
 * detalle en el panel.", o `null` si no hay ninguno.
 * @param {Array<object>|undefined} warnings
 * @returns {string|null}
 */
function construirWarningsNota(warnings) {
    const cantidad = (warnings ?? []).length;
    if (!cantidad) return null;
    return `Hay ${cantidad} gasto(s) de Fudo sin mapear; revisá el detalle en el panel.`;
}

/**
 * Marca de anulación, spec "Warnings and Void Marker".
 * @param {object} row
 * @returns {{fecha: string, motivo: string}|null}
 */
function construirAnulada(row) {
    if (row.estado !== 'anulada') return null;
    return { fecha: formatFechaCorta(row.anulada_at), motivo: row.anulada_motivo ?? '' };
}

/**
 * @param {object} row fila de `listLiquidaciones` (incluye `empleados: { nombre }`)
 * @returns {object} view-model — ver design.md "Interfaces / Contracts"
 */
export function construirComprobante(row) {
    const detalle = row.detalle ?? {};
    return {
        shortId: (row.id ?? '').slice(0, 8),
        empleadoNombre: row.empleados?.nombre ?? '',
        periodoDesde: formatFechaPeriodo(row.periodo_desde),
        periodoHasta: formatFechaPeriodo(row.periodo_hasta),
        fechaLiquidacion: formatFechaHora(row.creada_at),
        modalidad: MODALIDAD_LABELS[detalle.modo] ?? detalle.modo,
        cantidades: construirCantidades(detalle),
        montos: construirMontos(row, detalle),
        cap: construirCap(detalle.descuento_consumos),
        consumosItems: construirConsumosItems(detalle.consumos_items),
        adelantosItems: construirAdelantosItems(detalle.adelantos_items),
        otrosDescuentosItems: construirOtrosDescuentosItems(detalle.otros_descuentos_items),
        warningsNota: construirWarningsNota(detalle.warnings),
        anulada: construirAnulada(row),
        footer: FOOTER_TEXT,
    };
}
